import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { CapabilityBitFlags, telSysCapabilityBitFlags } from '../../CMEAddModify/Constants'
import { BusinessOption, IntegrationType } from '../../TelSysConfiguration/Constants/Constants'
import { isBitmaskMatching } from './BitwiseOperations'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { isCxiProductApplicable } from './CxProductApplicability'

export type CxiEntitlementVisibility = {
    showCxiUser: boolean
    showCxiData: boolean
    isApiOnlyMode: boolean
}

const INTEGRATED_CXI_TELSYS_TYPES: TelSysTypes[] = [
    TelSysTypes.MS_TEAMS,
    TelSysTypes.WEBEX_CALLING,
    TelSysTypes.BROADSOFT_BROADWORKS
]

/**
 * Resolve which CXI entitlement controls should be available for a TelSys.
 *
 * @param telSysType - Selected Telephony Server type.
 * @param isIntegratedCxiEnabled - Whether the selected integrated TelSys currently has CXI capability and feature support.
 *
 * @returns Visibility flags for CXI User / CXI Data controls.
 */
export const getCxiEntitlementVisibility = (
    telSysType: Nullable<TelSysTypes>,
    isIntegratedCxiEnabled: boolean
): CxiEntitlementVisibility => {
    if (telSysType === TelSysTypes.CXI_API) {
        return {
            showCxiUser: false,
            showCxiData: true,
            isApiOnlyMode: true
        }
    }

    if (telSysType && INTEGRATED_CXI_TELSYS_TYPES.includes(telSysType) && isIntegratedCxiEnabled) {
        return {
            showCxiUser: true,
            showCxiData: true,
            isApiOnlyMode: false
        }
    }

    return {
        showCxiUser: false,
        showCxiData: false,
        isApiOnlyMode: false
    }
}

/**
 * Whether the telsys type is one that can carry CX Integration (MS Teams, Webex Calling or
 * BroadWorks) - i.e. it has a per-type CXI capability bit on the CME.
 *
 * @param telSysType Telephony server type, when known.
 *
 * @returns {@code true} for the three CXI-capable telsys types, {@code false} otherwise.
 */
export const isCxiTelSysType = (telSysType: Nullable<number | string>): boolean =>
    isVarNotEmpty(telSysType) && String(telSysType) in telSysCapabilityBitFlags

/**
 * Whether the CME has the CX Integration capability bit for the telsys type
 * ({@code CXI_TEAMS} / {@code CXI_WEBEX} / {@code CXI_BROADWORKS}).
 *
 * @param telSysType Telephony server type, when known.
 * @param cmeCapabilities The CME's capability bitmask, when loaded.
 *
 * @returns {@code true} when the matching CXI capability bit is set.
 */
export const isCxiCapabilityOn = (
    telSysType: Nullable<number | string>,
    cmeCapabilities: Nullable<number>
): boolean => {
    if (!isCxiTelSysType(telSysType) || IsVarEmpty(cmeCapabilities)) return false

    const capability: CapabilityBitFlags = telSysCapabilityBitFlags[telSysType as TelSysTypes]

    return isBitmaskMatching(cmeCapabilities, capability)
}

/**
 * The MS Teams business-option half of the CXI rule. A Teams telsys must be Legacy Teams or carry
 * the {@code CX-Integration} business option; business options are a Teams-only concept, so every
 * other telsys type passes. The integration type is compared case-insensitively, as its stored
 * casing varies across provisioning paths ('Legacy' vs 'legacy').
 *
 * @param telSysType Telephony server type, when known.
 * @param integrationType MS Teams integration type, when known.
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 *
 * @returns {@code true} unless the telsys is MS Teams and CX Integration does not apply to it.
 */
export const isCxiAllowedByTeamsBusinessOptions = (
    telSysType: Nullable<number | string>,
    integrationType: Nullable<IntegrationType>,
    businessIntegrationOptions: Definable<readonly BusinessOption[]>
): boolean =>
    telSysType !== TelSysTypes.MS_TEAMS ||
    isCxiProductApplicable(
        telSysType,
        // Narrow cast: lower-casing a value of the IntegrationType union yields the canonical member.
        integrationType?.toLowerCase() as Nullable<IntegrationType>,
        businessIntegrationOptions
    )

/**
 * Whether CX Integration (the CXI User licence, its summary tile and related Teams app downloads)
 * is available for a telsys: the CME must have the type's CXI capability and, for MS Teams only,
 * the business-option rule must pass. This is the single rule shared by Licence Management,
 * Add/Modify User, Users by Extension and the Telephony Server modal.
 *
 * @param telSysType Telephony server type, when known.
 * @param cmeCapabilities The CME's capability bitmask, when loaded.
 * @param integrationType MS Teams integration type, when known.
 * @param businessIntegrationOptions Parsed telsys business integration options, when known.
 *
 * @returns {@code true} when CX Integration should be offered.
 */
export const isCxiUserAvailable = (
    telSysType: Nullable<number | string>,
    cmeCapabilities: Nullable<number>,
    integrationType: Nullable<IntegrationType>,
    businessIntegrationOptions: Definable<readonly BusinessOption[]>
): boolean =>
    isCxiCapabilityOn(telSysType, cmeCapabilities) &&
    isCxiAllowedByTeamsBusinessOptions(telSysType, integrationType, businessIntegrationOptions)
